/* eslint-disable react/forbid-prop-types, react/require-default-props, react/no-array-index-key */
import React from "react";
import { companyToday } from "../../../helpers/companyTime";
import PropTypes from "prop-types";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import { formatMetricValue, formatCompact } from "./useDashboardMetrics";

const slug = (s) => String(s || "m").replace(/[^a-z0-9]/gi, "");

function ChartTip({ active, payload, label, metric }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="db-tip">
      <div className="t">{label}</div>
      <div className="v">{formatMetricValue(metric, payload[0].value)}</div>
    </div>
  );
}
ChartTip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
  metric: PropTypes.object,
};

const axisX = (
  <XAxis
    dataKey="timestamp"
    tickLine={false}
    axisLine={false}
    interval="preserveStartEnd"
    minTickGap={16}
    tickMargin={8}
  />
);

const axisY = (
  <YAxis
    tickLine={false}
    axisLine={false}
    width={38}
    tickFormatter={formatCompact}
    tickMargin={4}
  />
);

/* --------------------------- big number tile --------------------------- */
export function StatTile({ metric, summary, series, graphPos = "bottom" }) {
  const id = slug(metric?.id);
  const delta = summary?.deltaPct;
  const hasDelta = delta !== null && delta !== undefined && Number.isFinite(delta);
  const up = (delta || 0) >= 0;
  return (
    <div className={`wc-stat g-${graphPos}`}>
      <div className="wc-stat-info">
        <div className="wc-val">{formatMetricValue(metric, summary?.total)}</div>
        {hasDelta && (
          <div className={`wc-delta ${up ? "up" : "down"}`}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              {up ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
            </svg>
            {Math.abs(delta).toFixed(1)}% <span className="m">{summary?.periodLabel || "this month"}</span>
          </div>
        )}
      </div>
      {graphPos !== "off" && (
        <div className="wc-spark">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
              <defs>
                <linearGradient id={`sp-${id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--db-brand)" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="var(--db-brand)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke="var(--db-brand)"
                strokeWidth={2.2}
                fill={`url(#sp-${id})`}
                isAnimationActive={false}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
StatTile.propTypes = { metric: PropTypes.object, summary: PropTypes.object, series: PropTypes.array, graphPos: PropTypes.string };

/* ------------------------------ trend ---------------------------------- */
export function TrendChart({ metric, series, area }) {
  const id = slug(metric?.id);
  const Comp = area ? AreaChart : LineChart;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <Comp data={series} margin={{ top: 8, right: 10, bottom: 2, left: 0 }}>
        <defs>
          <linearGradient id={`ar-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--db-brand)" stopOpacity="0.24" />
            <stop offset="100%" stopColor="var(--db-brand)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        {axisX}
        {axisY}
        <Tooltip content={<ChartTip metric={metric} />} cursor={{ stroke: "var(--db-brand)", strokeOpacity: 0.25 }} />
        {area ? (
          <Area type="monotone" dataKey="value" stroke="var(--db-brand)" strokeWidth={2.4} fill={`url(#ar-${id})`} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
        ) : (
          <Line type="monotone" dataKey="value" stroke="var(--db-brand)" strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
        )}
      </Comp>
    </ResponsiveContainer>
  );
}
TrendChart.propTypes = { metric: PropTypes.object, series: PropTypes.array, area: PropTypes.bool };

/* ------------------------------- bars ---------------------------------- */
export function BarChartW({ metric, series }) {
  const id = slug(metric?.id);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={series} margin={{ top: 8, right: 10, bottom: 2, left: 0 }}>
        <defs>
          <linearGradient id={`bar-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--db-brand-2)" />
            <stop offset="100%" stopColor="var(--db-brand)" />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        {axisX}
        {axisY}
        <Tooltip content={<ChartTip metric={metric} />} cursor={{ fill: "var(--db-brand-soft)" }} />
        <Bar dataKey="value" fill={`url(#bar-${id})`} radius={[4, 4, 0, 0]} maxBarSize={26} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}
BarChartW.propTypes = { metric: PropTypes.object, series: PropTypes.array };

/* ---------------------- donut: progress to target ---------------------- */
export function DonutW({ metric, summary }) {
  const total = summary?.total || 0;
  const target = summary?.target || 0;
  const pct = target > 0 ? Math.min(100, Math.round((total / target) * 100)) : 0;
  const data = target > 0
    ? [{ name: "done", value: Math.min(total, target) }, { name: "left", value: Math.max(0, target - total) }]
    : [{ name: "done", value: 1 }, { name: "left", value: 0 }];
  return (
    <div className="wc-donut">
      <div style={{ width: 108, height: 108, flex: "none", position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={38} outerRadius={52} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
              <Cell fill="var(--db-brand)" />
              <Cell fill="var(--tvd-tile, rgba(15,23,42,0.08))" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 20, fontWeight: 700, color: "var(--db-ink)" }}>
          {target > 0 ? `${pct}%` : "—"}
        </div>
      </div>
      <div className="lab">
        <b>{formatMetricValue(metric, total)}</b>
        {target > 0 ? `of ${formatMetricValue(metric, target)} target` : "no target set"}
      </div>
    </div>
  );
}
DonutW.propTypes = { metric: PropTypes.object, summary: PropTypes.object };

/* ------------------- gauge: score against the target -------------------- */
export function GaugeW({ metric, summary }) {
  const total = summary?.total || 0;
  const target = summary?.target || 0;
  const pct = target > 0 ? Math.min(1, total / target) : 0;
  const arc = Math.PI * 80; // semicircle length for r=80
  let note = "Set a target on this metric to track progress.";
  if (target > 0) {
    if (pct >= 1) note = "Target hit — amazing work!";
    else if (pct >= 0.7) note = "Almost there, keep pushing.";
    else if (pct >= 0.4) note = "Good pace — keep it going.";
    else note = "Just getting started.";
  }
  return (
    <div className="wc-gauge">
      <div className="wc-gauge-arc">
        <svg viewBox="0 0 200 108" aria-hidden="true">
          <path d="M20,100 A80 80 0 0 1 180,100" fill="none" stroke="var(--tvd-tile, rgba(15,23,42,0.08))" strokeWidth="17" strokeLinecap="round" />
          {target > 0 && (
            <path
              d="M20,100 A80 80 0 0 1 180,100"
              fill="none" stroke="var(--db-brand)" strokeWidth="17" strokeLinecap="round"
              strokeDasharray={`${Math.max(0.02, pct) * arc} 999`}
            />
          )}
        </svg>
        <div className="wc-gauge-mid">
          <b>{target > 0 ? `${Math.round(pct * 100)}%` : "—"}</b>
          <span>{target > 0 ? `of ${formatMetricValue(metric, target)} target` : "no target set"}</span>
        </div>
      </div>
      <div className="wc-gauge-val">{formatMetricValue(metric, total)} {summary?.periodLabel || ""}</div>
      <div className="wc-gauge-note">{note}</div>
    </div>
  );
}
GaugeW.propTypes = { metric: PropTypes.object, summary: PropTypes.object };

/* -------------------- dot grid: columns of little squares --------------- */
export function DotMatrixW({ metric, series }) {
  // A month of daily points reads better bucketed by week.
  let pts = series || [];
  if (pts.length > 14) {
    const weeks = [];
    pts.forEach((p, i) => {
      const w = Math.floor(i / 7);
      if (!weeks[w]) weeks[w] = { timestamp: `Wk ${w + 1}`, value: 0 };
      weeks[w].value += p.value;
    });
    pts = weeks;
  }
  const max = Math.max(...pts.map((p) => p.value), 1);
  const ROWS = 9;
  const U = 13; // cell pitch
  const S = 9; // square size
  const width = Math.max(pts.length * (U + 14), 1);
  const height = ROWS * U + 18;
  return (
    <div className="wc-dots">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" width="100%" height="100%">
        {pts.map((p, c) => {
          const filled = p.value > 0 ? Math.max(1, Math.round((p.value / max) * ROWS)) : 0;
          const x = c * (U + 14) + 7;
          return (
            <g key={c}>
              <title>{`${p.timestamp}: ${formatMetricValue(metric, p.value)}`}</title>
              {Array.from({ length: ROWS }, (_, r) => (
                <rect
                  key={r}
                  x={x}
                  y={(ROWS - 1 - r) * U}
                  width={S}
                  height={S}
                  rx="2"
                  fill="var(--db-brand)"
                  opacity={r < filled ? 0.55 + (r / ROWS) * 0.45 : 0.07}
                />
              ))}
              <text x={x + S / 2} y={height - 4} textAnchor="middle" fontSize="9" fill="#98a2b3">
                {p.timestamp}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
DotMatrixW.propTypes = { metric: PropTypes.object, series: PropTypes.array };

/* ------------- heatmap: weekday × week intensity from daily data --------- */
export function HeatmapW({ metric, series }) {
  const pts = series || [];
  const max = Math.max(...pts.map((p) => p.value), 1);
  let colLabels = [];
  let rows = [];
  if (pts.length >= 28) {
    // Current month, daily — weekday columns, one row per week.
    colLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const offset = (companyToday().startOf("month").day() + 6) % 7; // Monday-first
    const cells = [...Array.from({ length: offset }, () => null), ...pts];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    const last = rows[rows.length - 1];
    while (last && last.length < 7) last.push(null);
  } else if (pts.length === 7) {
    colLabels = pts.map((p) => p.timestamp);
    rows = [pts];
  } else {
    // Year (or short) series — one row of cells, labelled directly.
    colLabels = pts.map((p) => p.timestamp);
    rows = [pts];
  }
  return (
    <div className="wc-heat">
      <div className="wc-heat-grid" style={{ gridTemplateColumns: `repeat(${colLabels.length}, 1fr)` }}>
        {colLabels.map((l, i) => (
          <span key={`h-${i}`} className="wc-heat-lab">{l}</span>
        ))}
        {rows.map((row, r) =>
          row.map((cell, c) => (
            <span
              key={`${r}-${c}`}
              className="wc-heat-cell"
              title={cell ? `${cell.timestamp}: ${formatMetricValue(metric, cell.value)}` : ""}
              style={cell ? { opacity: 0.08 + 0.92 * (cell.value / max) } : { opacity: 0.03 }}
            />
          ))
        )}
      </div>
    </div>
  );
}
HeatmapW.propTypes = { metric: PropTypes.object, series: PropTypes.array };

/* ------------- compare: several metrics on one chart, one card ---------- */
function CompareTip({ active, payload, label, items }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="db-tip">
      <div className="t">{label}</div>
      {payload.map((p) => {
        const idx = Number(String(p.dataKey).slice(1));
        const it = items[idx];
        return (
          <div key={p.dataKey} className="v" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: it?.color, flex: "none" }} />
            {formatMetricValue(it?.metric, p.value)}
          </div>
        );
      })}
    </div>
  );
}
CompareTip.propTypes = { active: PropTypes.bool, payload: PropTypes.array, label: PropTypes.string, items: PropTypes.array };

export function CompareW({ items }) {
  const list = (items || []).filter((it) => it.metric);
  const base = list[0]?.series || [];
  const data = base.map((pt, i) => {
    const row = { timestamp: pt.timestamp };
    list.forEach((it, k) => {
      row[`m${k}`] = it.series?.[i]?.value || 0;
    });
    return row;
  });
  return (
    <div className="wc-cmp">
      <div className="wc-cmp-head">
        {list.map((it) => (
          <div key={it.metric.id} className="wc-cmp-item">
            <span className="dot" style={{ background: it.color }} />
            <span className="n">{it.metric.title}</span>
            <b>{formatMetricValue(it.metric, it.summary?.total)}</b>
          </div>
        ))}
        {list.length === 1 && (
          <span className="wc-cmp-hint">Edit this widget to add metrics to compare.</span>
        )}
      </div>
      <div className="wc-cmp-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 10, bottom: 2, left: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            {axisX}
            {axisY}
            <Tooltip content={<CompareTip items={list} />} cursor={{ stroke: "var(--db-brand)", strokeOpacity: 0.25 }} />
            {list.map((it, k) => (
              <Line key={it.metric.id} type="monotone" dataKey={`m${k}`} stroke={it.color} strokeWidth={2.2} dot={false} activeDot={{ r: 3.5 }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
CompareW.propTypes = { items: PropTypes.array };

/* ------------------------------ table ---------------------------------- */
export function TableW({ metric, series }) {
  const rows = (series || []).filter((r) => r.value !== 0).slice().reverse();
  const list = rows.length ? rows : series || [];
  return (
    <div className="wc-table">
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th style={{ textAlign: "right" }}>{metric?.title || "Value"}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.timestamp}>
              <td>{/^\d+$/.test(r.timestamp) ? `Day ${r.timestamp}` : r.timestamp}</td>
              <td className="v">{formatMetricValue(metric, r.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
TableW.propTypes = { metric: PropTypes.object, series: PropTypes.array };
